import { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  MdReceiptLong, MdAdd, MdClose, MdEventRepeat, MdEdit, MdDeleteOutline,
  MdCheckCircle, MdCalendarToday, MdStorefront, MdNotificationsActive,
  MdCloudUpload, MdInsertDriveFile, MdOpenInNew, MdBlock, MdWarningAmber,
  MdArrowForward,
} from 'react-icons/md';
import * as billSvc from '../../services/billService';
import useImpersonation from '../../hooks/useImpersonation';
import useTheme from '../../context/ThemeContext';
import DualDatePicker from '../common/DualDatePicker';
import { adToBs } from '../../utils/calendar';

// Shared with the Expenses form so a paid bill maps 1:1 onto an expense category.
const CATEGORIES = [
  { value: 'rent', label: 'Rent' },
  { value: 'electricity', label: 'Electricity' },
  { value: 'water', label: 'Water' },
  { value: 'internet', label: 'Internet' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'trainer_payment', label: 'Trainer Payment' },
  { value: 'cleaning', label: 'Cleaning' },
  { value: 'transport', label: 'Transport' },
  { value: 'tournament', label: 'Tournament' },
  { value: 'repair_maintenance', label: 'Repair and Maintenance' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'office_supplies', label: 'Office Supplies' },
  { value: 'software', label: 'Software' },
  { value: 'staff_salary', label: 'Staff Salary' },
  { value: 'other', label: 'Other' },
];

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'khalti', label: 'Khalti' },
  { value: 'esewa', label: 'eSewa' },
  { value: 'fonepay_qr', label: 'Fonepay QR' },
  { value: 'connectips', label: 'ConnectIPS' },
  { value: 'other', label: 'Other' },
];

const KTM_OFFSET_MS = (5 * 60 + 45) * 60 * 1000; // UTC+5:45
const pad2 = (n) => String(n).padStart(2, '0');
const fmtNPR = (n) => `NPR ${Number(n || 0).toLocaleString()}`;
const catLabel = (v) => CATEGORIES.find(c => c.value === v)?.label || v;

// Read a stored UTC instant (KTM-midnight) back as its Kathmandu calendar Y/M/D — no
// toISOString, so no off-by-one when the viewer isn't in Nepal.
const ktmYmd = (iso) => {
  const d = new Date(new Date(iso).getTime() + KTM_OFFSET_MS);
  return { y: d.getUTCFullYear(), m: d.getUTCMonth() + 1, d: d.getUTCDate() };
};
const ktmTodayYmd = () => {
  const d = new Date(Date.now() + KTM_OFFSET_MS);
  return { y: d.getUTCFullYear(), m: d.getUTCMonth() + 1, d: d.getUTCDate() };
};
const ymdStr = ({ y, m, d }) => `${y}-${pad2(m)}-${pad2(d)}`;
const daysBetween = (a, b) => Math.round((Date.UTC(a.y, a.m - 1, a.d) - Date.UTC(b.y, b.m - 1, b.d)) / 86400000);

// "26 Jul 2026 · Shrawan 11, 2083" from a stored KTM-midnight instant.
const dateLabel = (iso) => {
  const { y, m, d } = ktmYmd(iso);
  const local = new Date(y, m - 1, d);
  const ad = local.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  try {
    const bs = adToBs(local);
    return `${ad} · ${bs.monthName} ${bs.day}, ${bs.year}`;
  } catch {
    return ad;
  }
};

// Live due-date chip. Explicit tints (never bg-primary/N): >7 days muted, ≤7 amber tint,
// due today solid amber, overdue red.
function DueChip({ iso }) {
  const daysLeft = daysBetween(ktmYmd(iso), ktmTodayYmd());
  let text, tone;
  if (daysLeft < 0) {
    const n = Math.abs(daysLeft);
    text = `Overdue by ${n} day${n === 1 ? '' : 's'}`;
    tone = 'bg-red-500/15 text-red-400 border-red-500/25';
  } else if (daysLeft === 0) {
    text = 'Due today';
    tone = 'bg-amber-500 text-white border-amber-500'; // solid
  } else if (daysLeft === 1) {
    text = 'Due tomorrow';
    tone = 'bg-amber-500/15 text-amber-400 border-amber-500/25';
  } else if (daysLeft <= 7) {
    text = `Due in ${daysLeft} days`;
    tone = 'bg-amber-500/15 text-amber-400 border-amber-500/25';
  } else {
    text = `Due in ${daysLeft} days`;
    tone = 'bg-nav-hover text-muted border-divider';
  }
  return <span className={`inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full border ${tone}`}>{text}</span>;
}

const inpCls = 'w-full px-3 py-2.5 rounded-lg border text-sm outline-none transition-colors bg-nav-hover/40 border-divider focus:border-primary focus:ring-2 focus:ring-primary/30 text-secondary placeholder:text-subtle';
const lblCls = 'block text-xs font-semibold text-muted mb-1.5 uppercase tracking-wide';

// ── Add / Edit Bill modal ─────────────────────────────────────────────────────
function BillFormModal({ bill, existingBills, vendors, onClose, onSaved }) {
  const isEdit = !!bill?._id;
  const currentTheme = useTheme()?.currentTheme;
  const [form, setForm] = useState({
    title: bill?.title || '',
    amount: bill?.amount || '',
    dueDate: bill?.dueDate ? ymdStr(ktmYmd(bill.dueDate)) : ymdStr(ktmTodayYmd()),
    category: bill?.category || '',
    vendor: bill?.vendor || '',
    notes: bill?.notes || '',
    remindDaysBefore: bill?.remindDaysBefore ?? 3,
  });
  const [submitting, setSubmitting] = useState(false);
  const [dupDismissed, setDupDismissed] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // FLAW 5: non-blocking duplicate warning — same vendor + amount + due date as an existing
  // active bill. Purely client-side against already-loaded data; dismissible, never blocks.
  const duplicate = useMemo(() => {
    if (isEdit) return null;
    const v = form.vendor.trim().toLowerCase();
    const amt = Number(form.amount);
    if (!v || !(amt > 0) || !form.dueDate) return null;
    return (existingBills || []).find(b =>
      (b.status === 'upcoming' || b.status === 'overdue') &&
      (b.vendor || '').trim().toLowerCase() === v &&
      Number(b.amount) === amt &&
      ymdStr(ktmYmd(b.dueDate)) === form.dueDate
    ) || null;
  }, [isEdit, form.vendor, form.amount, form.dueDate, existingBills]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.amount || !form.dueDate || !form.category) {
      return toast.error('Please fill title, amount, due date and category');
    }
    if (Number(form.amount) <= 0) return toast.error('Amount must be greater than 0');

    setSubmitting(true);
    try {
      const payload = {
        title: form.title.trim(),
        amount: Number(form.amount),
        dueDate: form.dueDate,
        category: form.category,
        vendor: form.vendor.trim() || undefined,
        notes: form.notes.trim() || undefined,
        remindDaysBefore: Math.max(0, Math.min(30, Number(form.remindDaysBefore) || 0)),
      };
      if (isEdit) { await billSvc.updateBill(bill._id, payload); toast.success('Bill updated'); }
      else { await billSvc.createBill(payload); toast.success('Bill added'); }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save bill');
    } finally {
      setSubmitting(false);
    }
  };

  const primaryBtn = currentTheme === 'dark' ? 'bg-primary text-white' : 'bg-secondary text-card';

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="w-full sm:max-w-lg bg-card sm:rounded-2xl shadow-2xl flex flex-col" style={{ maxHeight: '92vh' }}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-divider">
          <h2 className="font-bold text-secondary flex items-center gap-2">
            <MdReceiptLong className="text-primary" />
            {isEdit ? 'Edit Bill' : 'Add Bill'}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-nav-hover text-muted transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center">
            <MdClose className="text-lg" />
          </button>
        </div>

        <form onSubmit={submit} className="overflow-y-auto px-5 py-5 space-y-4">
          <div>
            <label className={lblCls}>Title <span className="text-red-500">*</span></label>
            <input className={inpCls} value={form.title} onChange={e => set('title', e.target.value)}
              placeholder="e.g. AC Installation" required />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={lblCls}>Amount (NPR) <span className="text-red-500">*</span></label>
              <input className={inpCls} type="number" min="1" step="0.01" value={form.amount}
                onChange={e => set('amount', e.target.value)} placeholder="0" required />
            </div>
            <div>
              <label className={lblCls}>Due Date <span className="text-red-500">*</span></label>
              <DualDatePicker value={form.dueDate} onChange={v => set('dueDate', v)} />
            </div>
          </div>

          <div>
            <label className={lblCls}>Category <span className="text-red-500">*</span></label>
            <select className={inpCls} value={form.category} onChange={e => set('category', e.target.value)} required>
              <option value="">Select category</option>
              {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>

          <div>
            <label className={lblCls}>Payee <span className="text-subtle normal-case font-normal">(optional)</span></label>
            {/* FLAW 6: native autocomplete from this club's previously used vendors. */}
            <input className={inpCls} value={form.vendor} onChange={e => set('vendor', e.target.value)}
              placeholder="Vendor / person to pay" list="bill-vendor-list" autoComplete="off" />
            <datalist id="bill-vendor-list">
              {(vendors || []).map(v => <option key={v} value={v} />)}
            </datalist>
          </div>

          {/* FLAW 5: duplicate warning */}
          {duplicate && !dupDismissed && (
            <div className="flex items-start gap-2.5 p-3 rounded-xl bg-amber-500/15 border border-amber-500/25">
              <MdWarningAmber className="text-amber-400 text-lg flex-shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <p className="text-[13px] font-semibold text-amber-400">Possible duplicate</p>
                <p className="text-[12px] text-muted mt-0.5">
                  “{duplicate.title}” — same payee, amount and due date already exists. You can still add this if it's intentional.
                </p>
              </div>
              <button type="button" onClick={() => setDupDismissed(true)}
                className="p-1 rounded-lg text-muted hover:bg-amber-500/15 transition-colors flex-shrink-0" title="Dismiss">
                <MdClose className="text-base" />
              </button>
            </div>
          )}

          <div>
            <label className={lblCls}>Remind me — days before due</label>
            <input className={inpCls} type="number" min="0" max="30" step="1" value={form.remindDaysBefore}
              onChange={e => set('remindDaysBefore', e.target.value)} />
            <p className="text-[11px] text-subtle mt-1.5">
              {Number(form.remindDaysBefore) > 0
                ? `All admins get an email + notification ${form.remindDaysBefore} day(s) before, on the due date, then weekly while overdue.`
                : 'All admins get an email + notification on the due date, then weekly while overdue.'}
            </p>
          </div>

          <div>
            <label className={lblCls}>Notes <span className="text-subtle normal-case font-normal">(optional)</span></label>
            <textarea className={inpCls} rows={2} value={form.notes} onChange={e => set('notes', e.target.value)}
              placeholder="Anything worth noting" />
          </div>

          <div className="flex gap-2 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2.5 rounded-full border border-divider text-secondary text-sm font-medium hover:bg-nav-hover transition-colors min-h-[44px]">
              Cancel
            </button>
            <button type="submit" disabled={submitting}
              className={`flex-1 px-4 py-2.5 rounded-full text-sm font-semibold ${primaryBtn} hover:opacity-90 active:scale-[0.98] transition-all duration-150 disabled:opacity-50 min-h-[44px]`}>
              {submitting ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Bill'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Mark as Paid modal → records a normal expense + locks the bill ─────────────
function PayBillModal({ bill, onClose, onPaid }) {
  const currentTheme = useTheme()?.currentTheme;
  const fileRef = useRef();
  const [method, setMethod] = useState('cash');
  const [paidDate, setPaidDate] = useState(ymdStr(ktmTodayYmd())); // prefilled today (KTM)
  const [amount, setAmount] = useState(String(bill.amount)); // FLAW 2: prefilled with planned
  const [proof, setProof] = useState(null);       // { file, url, isImage }
  const [submitting, setSubmitting] = useState(false);

  const differs = Number(amount) > 0 && Number(amount) !== Number(bill.amount);

  const pickProof = (e) => {
    const f = (e.target.files || [])[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error('File is too large (max 5 MB)'); return; }
    const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf'];
    if (!allowed.includes(f.type)) { toast.error('Only JPEG, PNG, WEBP or PDF allowed'); return; }
    if (proof?.url) URL.revokeObjectURL(proof.url);
    setProof({ file: f, url: URL.createObjectURL(f), isImage: f.type.startsWith('image/') });
  };

  const clearProof = () => {
    if (proof?.url) URL.revokeObjectURL(proof.url);
    setProof(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!method) return toast.error('Select a payment method');
    if (!(Number(amount) > 0)) return toast.error('Amount paid must be greater than 0');
    if (!proof?.file) return toast.error('Payment proof is required');
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('paymentMethod', method);
      fd.append('paidDate', paidDate);
      fd.append('paidAmount', String(Number(amount)));
      fd.append('proof', proof.file);
      await billSvc.payBill(bill._id, fd);
      toast.success('Bill paid and recorded as an expense');
      onPaid();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to record payment');
    } finally {
      setSubmitting(false);
    }
  };

  const primaryBtn = currentTheme === 'dark' ? 'bg-primary text-white' : 'bg-secondary text-card';

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="w-full sm:max-w-md bg-card sm:rounded-2xl shadow-2xl flex flex-col" style={{ maxHeight: '92vh' }}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-divider">
          <h2 className="font-bold text-secondary flex items-center gap-2">
            <MdCheckCircle className="text-emerald-400" /> Mark as Paid
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-nav-hover text-muted transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center">
            <MdClose className="text-lg" />
          </button>
        </div>

        <form onSubmit={submit} className="overflow-y-auto px-5 py-5 space-y-4">
          <div className="bg-nav-hover/40 border border-divider rounded-xl p-4">
            <p className="text-sm font-semibold text-secondary">{bill.title}</p>
            <p className="text-[12px] text-muted mt-1">{catLabel(bill.category)}{bill.vendor ? ` · ${bill.vendor}` : ''}</p>
            <p className="text-[12px] text-subtle mt-1">Planned: {fmtNPR(bill.amount)}</p>
          </div>

          <div>
            <label className={lblCls}>Amount paid (NPR) <span className="text-red-500">*</span></label>
            <input className={inpCls} type="number" min="1" step="0.01" value={amount}
              onChange={e => setAmount(e.target.value)} required />
            {differs && (
              <p className="text-[11px] text-amber-400 mt-1.5">
                Differs from the planned {fmtNPR(bill.amount)} — the expense records what you actually paid.
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={lblCls}>Payment Method <span className="text-red-500">*</span></label>
              <select className={inpCls} value={method} onChange={e => setMethod(e.target.value)} required>
                {PAYMENT_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
            <div>
              <label className={lblCls}>Paid Date <span className="text-red-500">*</span></label>
              <DualDatePicker value={paidDate} onChange={setPaidDate} />
            </div>
          </div>

          {/* Compulsory payment proof — camera or file (works on phones). */}
          <div>
            <label className={lblCls}>Payment proof <span className="text-red-500">*</span></label>
            <input ref={fileRef} type="file" accept="image/*,application/pdf" onChange={pickProof} className="hidden" />
            {!proof ? (
              <button type="button" onClick={() => fileRef.current?.click()}
                className="w-full flex flex-col items-center justify-center gap-1.5 px-4 py-5 rounded-xl border-2 border-dashed border-divider text-muted hover:border-primary hover:text-secondary transition-colors min-h-[44px]">
                <MdCloudUpload className="text-2xl" />
                <span className="text-sm font-medium">Take photo or upload file</span>
                <span className="text-[11px] text-subtle">JPG, PNG, WEBP or PDF · max 5 MB</span>
              </button>
            ) : (
              <div className="flex items-center gap-3 p-2.5 rounded-xl border border-divider bg-nav-hover/40">
                {proof.isImage ? (
                  <img src={proof.url} alt="Proof preview" className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
                ) : (
                  <div className="w-12 h-12 rounded-lg bg-nav-hover flex items-center justify-center flex-shrink-0">
                    <MdInsertDriveFile className="text-xl text-muted" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-secondary truncate">{proof.file.name}</p>
                  <p className="text-[11px] text-subtle">{(proof.file.size / 1024).toFixed(0)} KB</p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button type="button" onClick={() => fileRef.current?.click()}
                    className="px-2.5 py-1.5 rounded-lg text-xs font-medium text-muted hover:bg-nav-hover hover:text-secondary transition-colors min-h-[40px]">
                    Replace
                  </button>
                  <button type="button" onClick={clearProof}
                    className="p-2 rounded-lg text-muted hover:bg-red-500/15 hover:text-red-400 transition-colors min-w-[40px] min-h-[40px] flex items-center justify-center">
                    <MdClose className="text-base" />
                  </button>
                </div>
              </div>
            )}
            <p className="text-[11px] text-subtle mt-1.5">Upload the bill receipt or payment screenshot.</p>
          </div>

          <p className="text-[12px] text-muted bg-nav-hover/40 border border-divider rounded-lg px-3 py-2.5">
            This records an approved expense of <span className="font-semibold text-secondary">{fmtNPR(Number(amount) || 0)}</span> and locks the bill. It will appear in Expenses (with the proof attached) and this month's totals.
          </p>

          <div className="flex gap-2 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2.5 rounded-full border border-divider text-secondary text-sm font-medium hover:bg-nav-hover transition-colors min-h-[44px]">
              Cancel
            </button>
            <button type="submit" disabled={submitting || !proof}
              className={`flex-1 px-4 py-2.5 rounded-full text-sm font-semibold ${primaryBtn} hover:opacity-90 active:scale-[0.98] transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]`}>
              {submitting ? 'Recording…' : 'Confirm Payment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Cancel Bill modal (distinct from delete) → status 'cancelled' + reason ─────
function CancelBillModal({ bill, onClose, onCancelled }) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) return toast.error('A short reason is required');
    setSubmitting(true);
    try {
      await billSvc.cancelBill(bill._id, reason.trim());
      toast.success('Bill cancelled');
      onCancelled();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to cancel bill');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="w-full sm:max-w-md bg-card sm:rounded-2xl shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-divider">
          <h2 className="font-bold text-secondary flex items-center gap-2">
            <MdBlock className="text-amber-400" /> Cancel Bill
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-nav-hover text-muted transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center">
            <MdClose className="text-lg" />
          </button>
        </div>
        <form onSubmit={submit} className="px-5 py-5 space-y-4">
          <p className="text-[13px] text-muted">
            Cancelling <span className="font-semibold text-secondary">{bill.title}</span> stops all reminders and marks it cancelled. It's kept for your records (not deleted).
          </p>
          <div>
            <label className={lblCls}>Reason <span className="text-red-500">*</span></label>
            <input className={inpCls} value={reason} onChange={e => setReason(e.target.value)} maxLength={200}
              placeholder="e.g. Vendor cancelled the order" required autoFocus />
          </div>
          <div className="flex gap-2 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2.5 rounded-full border border-divider text-secondary text-sm font-medium hover:bg-nav-hover transition-colors min-h-[44px]">
              Keep bill
            </button>
            <button type="submit" disabled={submitting}
              className="flex-1 px-4 py-2.5 rounded-full text-sm font-semibold bg-amber-500 text-white hover:opacity-90 active:scale-[0.98] transition-all duration-150 disabled:opacity-50 min-h-[44px]">
              {submitting ? 'Cancelling…' : 'Cancel Bill'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Recurring info modal — explains it's managed in Recurring Expenses ─────────
function RecurringInfoModal({ onClose }) {
  const navigate = useNavigate();
  const currentTheme = useTheme()?.currentTheme;
  const primaryBtn = currentTheme === 'dark' ? 'bg-primary text-white' : 'bg-secondary text-card';
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="w-full sm:max-w-sm bg-card sm:rounded-2xl shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-divider">
          <h2 className="font-bold text-secondary flex items-center gap-2">
            <MdEventRepeat className="text-primary" /> Recurring expense
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-nav-hover text-muted transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center">
            <MdClose className="text-lg" />
          </button>
        </div>
        <div className="px-5 py-5 space-y-4">
          <p className="text-[13px] text-muted">
            This entry is generated automatically by one of your <span className="font-semibold text-secondary">recurring expenses</span>. It isn't a manual bill, so it can't be edited or paid here. To change the amount, pause, or stop it, manage it in Recurring Expenses.
          </p>
          <button
            onClick={() => { onClose(); navigate('/admin/finance?tab=expenses'); }}
            className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold ${primaryBtn} hover:opacity-90 active:scale-[0.98] transition-all duration-150 min-h-[44px]`}
          >
            Go to Recurring Expenses <MdArrowForward className="text-base" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Section: Upcoming Bills ────────────────────────────────────────────────────
const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'upcoming', label: 'Upcoming' },
  { id: 'overdue', label: 'Overdue' },
  { id: 'paid', label: 'Paid' },
  { id: 'cancelled', label: 'Cancelled' },
];

export default function UpcomingBills() {
  const qc = useQueryClient();
  const currentTheme = useTheme()?.currentTheme;
  const { isReadOnly } = useImpersonation();
  const sectionRef = useRef(null);

  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [payTarget, setPayTarget] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [showRecurringInfo, setShowRecurringInfo] = useState(false);
  const [filter, setFilter] = useState('all');

  const { data, isPending } = useQuery({
    queryKey: ['bills'],
    queryFn: () => billSvc.getBills().then(r => r.data),
  });

  const { data: vendorData } = useQuery({
    queryKey: ['bill-vendors'],
    queryFn: () => billSvc.getBillVendors().then(r => r.data),
    staleTime: 60000,
  });

  // Deep link from reminder emails (…/finance?tab=overview#upcoming-bills) → scroll into view.
  useEffect(() => {
    if (typeof window !== 'undefined' && window.location.hash === '#upcoming-bills' && sectionRef.current) {
      sectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [isPending]);

  const deleteMutation = useMutation({
    mutationFn: (id) => billSvc.deleteBill(id),
    onSuccess: () => { toast.success('Bill deleted'); qc.invalidateQueries({ queryKey: ['bills'] }); },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed to delete'),
  });

  const afterMutate = () => {
    qc.invalidateQueries({ queryKey: ['bills'] });
    qc.invalidateQueries({ queryKey: ['bill-vendors'] });
    // A paid bill becomes an expense → refresh finance surfaces.
    qc.invalidateQueries({ queryKey: ['expenses'] });
    qc.invalidateQueries({ queryKey: ['expense-summary'] });
    qc.invalidateQueries({ queryKey: ['finance-summary'] });
    setShowForm(false);
    setEditTarget(null);
    setPayTarget(null);
    setCancelTarget(null);
  };

  const allBills   = data?.bills || [];
  const recurring  = data?.recurringUpcoming || [];
  const byStatus   = (s) => allBills.filter(b => b.status === s);
  const upcoming   = byStatus('upcoming');
  const overdue    = byStatus('overdue');
  const paid        = byStatus('paid').sort((a, b) => new Date(b.paidAt || 0) - new Date(a.paidAt || 0));
  const cancelled   = byStatus('cancelled').sort((a, b) => new Date(b.cancelledAt || 0) - new Date(a.cancelledAt || 0));

  const counts = {
    all: upcoming.length + overdue.length + recurring.length,
    upcoming: upcoming.length + recurring.length,
    overdue: overdue.length,
    paid: paid.length,
    cancelled: cancelled.length,
  };

  const byDue = (a, b) => new Date(a.dueDate) - new Date(b.dueDate);

  // Rows shown for the active filter.
  let rows = [];
  if (filter === 'all')       rows = [...upcoming, ...overdue, ...recurring].sort(byDue);
  else if (filter === 'upcoming') rows = [...upcoming, ...recurring].sort(byDue);
  else if (filter === 'overdue')  rows = [...overdue].sort(byDue);
  else if (filter === 'paid')     rows = paid;
  else if (filter === 'cancelled') rows = cancelled;

  // Summary strip (KTM current month).
  const today = ktmTodayYmd();
  const dueThisMonth = [...upcoming, ...overdue].reduce((s, b) => {
    const d = ktmYmd(b.dueDate);
    return (d.y === today.y && d.m === today.m) ? s + Number(b.amount || 0) : s;
  }, 0);

  const primaryBtn = currentTheme === 'dark' ? 'bg-primary text-white' : 'bg-secondary text-card';
  const canEdit = !isReadOnly;
  const activeChip = currentTheme === 'dark' ? 'bg-primary text-white border-primary' : 'bg-secondary text-card border-secondary';

  const emptyCopy = {
    all: 'Track bills before they’re due — never miss a payment.',
    upcoming: 'No upcoming bills right now.',
    overdue: 'Nothing overdue. ',
    paid: 'No paid bills yet.',
    cancelled: 'No cancelled bills.',
  };

  return (
    <div ref={sectionRef} id="upcoming-bills" className="bg-card rounded-2xl border border-divider p-5 shadow-sm scroll-mt-20">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <h3 className="text-[15px] font-semibold text-secondary flex items-center gap-2">
            <MdNotificationsActive className="text-primary text-base" /> Upcoming Bills
          </h3>
          {/* Summary strip */}
          <p className="text-[13px] text-muted mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
            <span><span className="font-semibold text-secondary">{upcoming.length}</span> upcoming</span>
            <span className="text-subtle">·</span>
            <span><span className="font-semibold text-secondary">{fmtNPR(dueThisMonth)}</span> due this month</span>
            <span className="text-subtle">·</span>
            <span className={overdue.length > 0 ? 'text-red-400 font-semibold' : ''}>{overdue.length} overdue</span>
          </p>
        </div>
        {canEdit && (
          <button
            onClick={() => { setEditTarget(null); setShowForm(true); }}
            className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-semibold ${primaryBtn} hover:opacity-90 active:scale-[0.98] transition-all duration-150 min-h-[44px] flex-shrink-0`}
          >
            <MdAdd className="text-base" /> Add Bill
          </button>
        )}
      </div>

      {/* Status filter chips */}
      <div className="flex items-center gap-1.5 mb-4 overflow-x-auto -mx-1 px-1 pb-1">
        {FILTERS.map(f => {
          const isActive = filter === f.id;
          return (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`flex items-center gap-1.5 px-3 rounded-full text-[13px] font-medium border whitespace-nowrap transition-colors min-h-[40px] flex-shrink-0 ${
                isActive ? activeChip : 'bg-transparent text-muted border-divider hover:bg-nav-hover'
              }`}
            >
              {f.label}
              <span className={`text-[11px] ${isActive ? 'opacity-80' : 'text-subtle'}`}>{counts[f.id]}</span>
            </button>
          );
        })}
      </div>

      {isPending ? (
        <div className="space-y-2">
          {[0, 1].map(i => <div key={i} className="h-16 bg-nav-hover rounded-xl animate-pulse" />)}
        </div>
      ) : rows.length === 0 ? (
        <div className="text-center py-10">
          <MdReceiptLong className="mx-auto text-3xl text-subtle mb-2" />
          <p className="text-sm text-muted">{emptyCopy[filter]}</p>
          {canEdit && (filter === 'all' || filter === 'upcoming') && (
            <button
              onClick={() => { setEditTarget(null); setShowForm(true); }}
              className={`mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold ${primaryBtn} hover:opacity-90 active:scale-[0.98] transition-all duration-150 min-h-[44px]`}
            >
              <MdAdd className="text-base" /> Add Bill
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((b) => {
            const isRecurring = b.source === 'recurring';

            // Paid row — locked, planned→paid when adjusted, View proof.
            if (b.status === 'paid') {
              const adj = b.paidAmount != null && Number(b.paidAmount) !== Number(b.amount);
              return (
                <div key={b._id} className="flex items-center gap-3 p-3 rounded-xl border border-divider bg-nav-hover/20">
                  <MdCheckCircle className="text-emerald-400 text-lg flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-secondary truncate">{b.title}</p>
                    <p className="text-[12px] text-muted mt-0.5">
                      Paid{b.paidAt ? ` · ${dateLabel(b.paidAt)}` : ''} · {catLabel(b.category)}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <div className="text-right">
                      {adj ? (
                        <p className="text-sm font-semibold text-secondary whitespace-nowrap">
                          <span className="text-subtle line-through font-normal text-[12px]">{fmtNPR(b.amount)}</span>{' → '}{fmtNPR(b.paidAmount)}
                        </p>
                      ) : (
                        <p className="text-sm font-semibold text-secondary whitespace-nowrap">{fmtNPR(b.paidAmount ?? b.amount)}</p>
                      )}
                    </div>
                    {b.proofUrl && (
                      <a href={b.proofUrl} target="_blank" rel="noreferrer" title="View payment proof"
                        className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline min-h-[40px] px-1">
                        <MdOpenInNew className="text-sm" /> Proof
                      </a>
                    )}
                  </div>
                </div>
              );
            }

            // Cancelled row — muted, shows reason.
            if (b.status === 'cancelled') {
              return (
                <div key={b._id} className="flex items-center gap-3 p-3 rounded-xl border border-divider bg-nav-hover/10">
                  <MdBlock className="text-muted text-lg flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-muted truncate">{b.title}</p>
                    <p className="text-[12px] text-subtle mt-0.5 truncate">
                      Cancelled{b.cancelReason ? ` · ${b.cancelReason}` : ''}
                    </p>
                  </div>
                  <p className="text-sm font-medium text-subtle whitespace-nowrap flex-shrink-0">{fmtNPR(b.amount)}</p>
                </div>
              );
            }

            // Active (upcoming / overdue / recurring preview) row.
            return (
              <div key={b._id} className="flex items-center gap-3 p-3 rounded-xl border border-divider bg-nav-hover/30">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-secondary truncate">{b.title}</p>
                    <DueChip iso={b.dueDate} />
                    {isRecurring && (
                      <button
                        onClick={() => setShowRecurringInfo(true)}
                        className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-nav-hover text-muted border border-divider hover:text-secondary hover:border-primary transition-colors"
                        title="How is this managed?"
                      >
                        <MdEventRepeat className="text-[13px]" /> Recurring
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-1 text-[12px] text-muted flex-wrap">
                    <span className="inline-flex items-center gap-1"><MdCalendarToday className="text-[13px]" />{dateLabel(b.dueDate)}</span>
                    <span className="text-subtle">·</span>
                    <span>{catLabel(b.category)}</span>
                    {b.vendor && (<><span className="text-subtle">·</span><span className="inline-flex items-center gap-1"><MdStorefront className="text-[13px]" />{b.vendor}</span></>)}
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  <p className="text-sm font-semibold text-secondary whitespace-nowrap">{fmtNPR(b.amount)}</p>
                  {isRecurring ? (
                    <button onClick={() => setShowRecurringInfo(true)} title="Managed in Recurring Expenses"
                      className="text-[11px] text-subtle hover:text-secondary whitespace-nowrap px-2 py-1 rounded-lg hover:bg-nav-hover transition-colors min-h-[40px]">
                      Auto
                    </button>
                  ) : canEdit && (
                    <div className="flex items-center gap-0.5">
                      <button onClick={() => setPayTarget(b)} title="Mark as paid"
                        className="p-2 rounded-lg text-emerald-400 hover:bg-emerald-500/15 transition-colors min-w-[40px] min-h-[40px] flex items-center justify-center">
                        <MdCheckCircle className="text-lg" />
                      </button>
                      <button onClick={() => { setEditTarget(b); setShowForm(true); }} title="Edit"
                        className="p-2 rounded-lg text-muted hover:bg-nav-hover hover:text-secondary transition-colors min-w-[40px] min-h-[40px] flex items-center justify-center">
                        <MdEdit className="text-lg" />
                      </button>
                      <button onClick={() => setCancelTarget(b)} title="Cancel bill"
                        className="p-2 rounded-lg text-muted hover:bg-amber-500/15 hover:text-amber-400 transition-colors min-w-[40px] min-h-[40px] flex items-center justify-center">
                        <MdBlock className="text-lg" />
                      </button>
                      <button onClick={() => { if (window.confirm(`Delete the bill "${b.title}"? This removes it entirely — to keep it for records, cancel instead.`)) deleteMutation.mutate(b._id); }} title="Delete"
                        className="p-2 rounded-lg text-muted hover:bg-red-500/15 hover:text-red-400 transition-colors min-w-[40px] min-h-[40px] flex items-center justify-center">
                        <MdDeleteOutline className="text-lg" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showForm && (
        <BillFormModal
          bill={editTarget}
          existingBills={allBills}
          vendors={vendorData?.vendors}
          onClose={() => { setShowForm(false); setEditTarget(null); }}
          onSaved={afterMutate}
        />
      )}
      {payTarget && <PayBillModal bill={payTarget} onClose={() => setPayTarget(null)} onPaid={afterMutate} />}
      {cancelTarget && <CancelBillModal bill={cancelTarget} onClose={() => setCancelTarget(null)} onCancelled={afterMutate} />}
      {showRecurringInfo && <RecurringInfoModal onClose={() => setShowRecurringInfo(false)} />}
    </div>
  );
}
